
import React, { useState } from 'react';
import { ProjectProfile, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { X, Send, Briefcase } from 'lucide-react';

interface ProjectApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string, price: number) => void;
  project: ProjectProfile;
  language: Language;
}

export const ProjectApplicationModal: React.FC<ProjectApplicationModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  project, 
  language 
}) => {
  const t = TRANSLATIONS[language];
  const [pitch, setPitch] = useState('');
  const [price, setPrice] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pitch.trim()) {
      onSubmit(pitch, Number(price) || 0);
      setPitch('');
      setPrice('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
             <div className="bg-indigo-900/50 p-2 rounded-lg border border-indigo-500/20 text-indigo-400">
                <Briefcase size={20} />
             </div>
             <div>
                <h2 className="text-xl font-bold text-white">{t.applyTo}</h2>
                <p className="text-sm text-slate-400">{project.name}</p>
             </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Project Summary */}
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 text-sm text-slate-300">
              <span className="text-slate-500 block text-xs uppercase mb-1">{t.budgetRange}</span>
              <span className="font-bold text-white text-lg">{project.budgetRange}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t.pitchMessage}</label>
            <textarea 
              rows={5}
              required
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
              placeholder={t.pitchPlaceholder}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t.proposedRate}</label>
            <div className="relative">
                <span className="absolute left-4 top-2.5 text-slate-500">$</span>
                <input 
                type="number" 
                required
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="1000"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-8 pr-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
            </div>
          </div>

          <div className="pt-2 flex gap-3">
             <button 
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
             >
                {t.cancel}
             </button>
             <button 
                type="submit"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
             >
                <Send size={18} /> {t.sendApplication}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};
